import * as React from "react";
import { QueryClient, QueryCache, MutationCache } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { createAsyncStoragePersister } from "@tanstack/query-async-storage-persister";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { UnauthorizedError, ForbiddenError } from "@src/features/auth/services/authenticated-fetch";
import { getCachingConfig } from "@src/config/caching-config";
import { router } from "expo-router";

const millisecondsInDay = 1000 * 60 * 60 * 24;

/**
 * Handle authentication errors globally for all queries and mutations.
 *
 * - 401 Unauthorized: Clear auth and redirect to login
 * - 403 Forbidden: Let individual screens handle access-denied UX so we don't
 *   take over the entire app shell (for example, hiding the tab bar).
 */
function handleAuthError(error: unknown) {
  if (error instanceof UnauthorizedError) {
    console.log("[QueryClient] Unauthorized error - clearing auth");
    // Lazy require to avoid circular dependency with auth-store
    const { useAuthStore } = require("@src/features/auth/auth-store");
    useAuthStore.getState().clearAuth();
    // AuthProvider will handle redirect to Auth0
    router.replace("/");
  }
}

const asyncStoragePersister = createAsyncStoragePersister({
  storage: AsyncStorage,
  throttleTime: 1000,
});

let queryClient: QueryClient | null = null;

const getQueryClient = (): QueryClient => {
  if (queryClient) return queryClient;

  const cachingConfig = getCachingConfig();

  queryClient = new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query) => {
        console.error("Query error:", {
          error,
          queryKey: query.queryKey,
          queryHash: query.queryHash,
          state: query.state,
        });
        // Handle auth errors globally
        handleAuthError(error);
      },
    }),
    mutationCache: new MutationCache({
      onError: (error, variables, context) => {
        console.error("Mutation error:", {
          error,
          variables,
          context,
        });
        // Handle auth errors globally
        handleAuthError(error);
      },
    }),
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        retry: (failureCount, error) => {
          // Don't retry on auth errors
          if (error instanceof UnauthorizedError || error instanceof ForbiddenError) {
            return false;
          }
          return failureCount < 1;
        },
        staleTime: cachingConfig.globalQueries.staleTimeMs,
      },
    },
  });

  return queryClient;
};

/**
 * Clear all React Query cache and persisted data
 * Should be called on logout to prevent data leakage between users
 */
export async function clearQueryCache(): Promise<void> {
  try {
    // Clear persisted cache from AsyncStorage
    // The persister stores data under a specific key - we need to clear it
    // The default key is 'REACT_QUERY_OFFLINE_CACHE' but we should use the persister's method
    try {
      // Try to use the persister's removeClient method if available
      if (typeof asyncStoragePersister.removeClient === "function") {
        await asyncStoragePersister.removeClient();
      } else {
        // Fallback: manually clear the AsyncStorage key used by the persister
        // The default key is 'REACT_QUERY_OFFLINE_CACHE'
        await AsyncStorage.removeItem("REACT_QUERY_OFFLINE_CACHE");
      }
    } catch (persistError) {
      console.warn("[QueryClient] Failed to clear persisted cache, trying fallback:", persistError);
      // Fallback: try to clear the default key
      await AsyncStorage.removeItem("REACT_QUERY_OFFLINE_CACHE");
    }
  } catch (error) {
    console.error("[QueryClient] Failed to clear query cache:", error);
    // Don't throw - continue with logout even if cache clearing fails
  }
  // NOW clear in-memory cache
  getQueryClient().clear();
}

export function AppPersistQueryClientProvider({ children }: { children: React.ReactNode }) {
  return (
    <PersistQueryClientProvider
      client={getQueryClient()}
      persistOptions={{
        persister: asyncStoragePersister,
        maxAge: millisecondsInDay * 30,
        dehydrateOptions: {
          shouldDehydrateQuery: (query) => {
            const key = query.queryKey[0];
            if (
              key &&
              typeof key === "object" &&
              "feature" in key &&
              "resource" in key &&
              (key as { feature: string; resource: string }).feature === "media" &&
              (key as { feature: string; resource: string }).resource === "snapshots"
            ) {
              // Signed URLs expire quickly; selected data must stay JSON-serialisable. Never persist snapshots.
              return false;
            }
            // Persist successful queries
            // Also persist queries with errors IF they have cached data (for offline-first support)
            // This ensures cached data is available even after network errors
            const hasData = query.state.data !== undefined && query.state.data !== null;
            return query.state.status === "success" || (query.state.status === "error" && hasData);
          },
        },
      }}
      onSuccess={() => {
        // After restoring from cache, resume any paused mutations
        // and mark queries that were pending as stale
        getQueryClient().resumePausedMutations();
      }}
    >
      {children}
    </PersistQueryClientProvider>
  );
}
